/**
 * Advanced Setlist Simulation Engine
 * 
 * Inspired by HitlistSim_Yellow - uses sophisticated algorithms:
 * - Ornstein-Uhlenbeck process for mean-reverting random walks
 * - Softmax probability distribution for vote allocation
 * - Multiple noise models (Gaussian, Poisson, None)
 * - Burst mechanics (popularity waves)
 * - Jump mechanics (sudden attention spikes)
 * - Priority bias to keep top 20 songs near top 10
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Softmax function - converts scores to probability distribution
 */
function softmax(scores, beta = 1) {
  if (!scores.length) return [];
  const scaled = scores.map((x) => beta * x);
  const max = Math.max(...scaled);
  const exps = scaled.map((x) => Math.exp(x - max));
  const sum = exps.reduce((a, b) => a + b, 0);
  return exps.map((e) => (sum > 0 ? e / sum : 1 / scores.length));
}

/**
 * Clamp value between min and max
 */
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Gaussian random number generator (Box-Muller transform)
 */
function randn() {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

/**
 * Poisson sampler (Knuth algorithm for small lambda, normal approximation for large)
 */
function samplePoisson(lambda) {
  if (lambda <= 0) return 0;
  if (lambda > 30) {
    return Math.max(0, Math.round(lambda + Math.sqrt(lambda) * randn()));
  }
  let L = Math.exp(-lambda), k = 0, p = 1;
  do { k++; p *= Math.random(); } while (p > L);
  return k - 1;
}

/**
 * Weighted random choice based on probability array
 */
function weightedChoice(probs) {
  const r = Math.random();
  let acc = 0;
  for (let i = 0; i < probs.length; i++) {
    acc += probs[i];
    if (r <= acc) return i;
  }
  return probs.length - 1;
}

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

export const DEFAULT_SIMULATION_PARAMS = {
  // Core dynamics (Ornstein-Uhlenbeck process)
  theta: 0.15,        // Mean reversion speed (how fast songs return to baseline)
  mu: 0,              // Long-run mean of strength (normalized to 0)
  sigma: 0.25,        // Volatility (how much random movement)
  beta: 2.0,          // Softmax temperature (higher = sharper competition)
  alpha: 0.25,        // EMA smoothing for score updates
  
  // Timing
  updateInterval: 45000,  // 45 seconds between updates
  tickMs: 500,            // Internal simulation tick (not used in background service)
  
  // Ranking mode
  rankingMode: 'cumulative', // 'cumulative' | 'snapshot' - how rankings are calculated
  
  // Randomness controls
  noiseModel: 'gaussian', // 'gaussian' | 'poisson' | 'none'
  crowdJitterPct: 0.10,   // Std deviation of crowd size variation
  commonShock: 0.05,      // Common-mode strength shock per √dt
  probSmoothing: 0.30,    // Probability stickiness [0..0.95]
  
  // Burst mechanics (popularity waves)
  burstProbPerSec: 0.05,  // Probability of burst per second
  burstMean: 15,          // Average burst size in "votes"
  burstJitterPct: 0.60,   // Variance in burst size
  
  // Jump mechanics (sudden attention spikes)
  jumpProbPerSec: 0.02,   // Probability of jump per second
  jumpStd: 0.60,          // Standard deviation of jump magnitude
  
  // Priority bias (keeps top 20 near top 10)
  biasEnabled: true,
  biasPower: 2.5,         // Base bias magnitude for #1 song (increased for cumulative mode)
  biasDecay: 0.15,        // Exponential decay rate down the list (slower decay = more songs benefit)
  
  // Admin override
  adminOverrideDelay: 300000, // 5 minutes pause after admin update
};

// ============================================================================
// PRESET PROFILES
// ============================================================================

export const SIMULATION_PRESETS = {
  mainstream: {
    name: 'Mainstream',
    description: 'Smooth; less volatile; gradual changes',
    params: {
      rankingMode: 'cumulative',
      alpha: 0.28,
      theta: 0.16,
      sigma: 0.22,
      beta: 2.1,
      noiseModel: 'gaussian',
      crowdJitterPct: 0.08,
      commonShock: 0.04,
      probSmoothing: 0.35,
      burstProbPerSec: 0.04,
      burstMean: 12,
      burstJitterPct: 0.5,
      jumpProbPerSec: 0.015,
      jumpStd: 0.5,
      biasPower: 2.5,
      biasDecay: 0.15,
      biasEnabled: true,
    }
  },
  
  day1: {
    name: 'Day 1 Fans',
    description: 'More love for early era; extra volatility and occasional jumps',
    params: {
      rankingMode: 'cumulative',
      alpha: 0.24,
      theta: 0.14,
      sigma: 0.28,
      beta: 1.8,
      noiseModel: 'poisson',
      crowdJitterPct: 0.15,
      commonShock: 0.06,
      probSmoothing: 0.25,
      burstProbPerSec: 0.06,
      burstMean: 18,
      burstJitterPct: 0.7,
      jumpProbPerSec: 0.03,
      jumpStd: 0.7,
      biasPower: 2.3,
      biasDecay: 0.14,
      biasEnabled: true,
    }
  },
  
  festival: {
    name: 'Festival/Viral',
    description: 'Louder crowd; bigger waves; sharper peaks (bursts & jumps)',
    params: {
      rankingMode: 'cumulative',
      alpha: 0.20,
      theta: 0.12,
      sigma: 0.30,
      beta: 2.3,
      noiseModel: 'poisson',
      crowdJitterPct: 0.25,
      commonShock: 0.08,
      probSmoothing: 0.18,
      burstProbPerSec: 0.12,
      burstMean: 28,
      burstJitterPct: 0.9,
      jumpProbPerSec: 0.05,
      jumpStd: 0.9,
      biasPower: 2.8,
      biasDecay: 0.13,
      biasEnabled: true,
    }
  },
  
  lofi: {
    name: 'Late-night / Lofi',
    description: 'Chill; slower updates; minimal randomness; deep cuts surface slowly',
    params: {
      rankingMode: 'cumulative',
      alpha: 0.16,
      theta: 0.10,
      sigma: 0.16,
      beta: 1.6,
      noiseModel: 'none',
      crowdJitterPct: 0.06,
      commonShock: 0.03,
      probSmoothing: 0.5,
      burstProbPerSec: 0.02,
      burstMean: 8,
      burstJitterPct: 0.4,
      jumpProbPerSec: 0.01,
      jumpStd: 0.35,
      biasPower: 2.2,
      biasDecay: 0.16,
      biasEnabled: true,
    }
  }
};

// ============================================================================
// SIMULATION ENGINE
// ============================================================================

/**
 * Calculate priority bias for each song based on its position in top 20
 * Songs in top 20 get exponentially decaying bias to keep them near top 10
 */
function calculatePriorityBias(song, songIndex, params, top20Titles) {
  if (!params.biasEnabled) return 0;
  
  const priorityIndex = top20Titles.indexOf(song.title);
  if (priorityIndex === -1) return 0; // Not in top 20
  
  // Exponential decay: bias = biasPower * exp(-biasDecay * index)
  return params.biasPower * Math.exp(-params.biasDecay * priorityIndex);
}

/**
 * Main simulation update function
 * 
 * @param {Array} songs - Current song list with scores
 * @param {Object} params - Simulation parameters
 * @param {Array} top20Titles - Array of top 20 song titles for priority bias
 * @param {Object} previousState - Previous simulation state (for momentum, smoothing)
 * @returns {Array} Updated songs with new scores and ranks
 */
export function simulateSetlistUpdate(songs, params = DEFAULT_SIMULATION_PARAMS, top20Titles = [], previousState = null, debugMode = false) {
  // Merge with defaults
  const p = { ...DEFAULT_SIMULATION_PARAMS, ...params };
  
  // Time step in seconds (for probability calculations)
  const dt = p.updateInterval / 1000;
  
  // Initialize or retrieve previous state (handle null/undefined)
  const safeState = previousState || {};
  const prevStrengths = safeState.strengths || {};
  const prevProbs = safeState.probs || null;
  const prevTotalVotes = safeState.totalVotes || {}; // For cumulative mode
  
  // ========================================================================
  // STEP 1: Update strengths using Ornstein-Uhlenbeck process
  // ========================================================================
  
  // Common shock affects all songs equally
  const commonShockValue = p.commonShock > 0 ? p.commonShock * Math.sqrt(dt) * randn() : 0;
  
  const updatedSongs = songs.map((song, index) => {
    // Get or initialize strength (latent "attention" score)
    const currentStrength = prevStrengths[song.title] !== undefined 
      ? prevStrengths[song.title] 
      : (song.score - 0.5) * 2; // Convert score [0,1] to strength around 0
    
    // OU process: dX = θ(μ - X)dt + σ√dt * dW
    const drift = p.theta * (p.mu - currentStrength) * dt;
    const diffusion = p.sigma * Math.sqrt(dt) * randn();
    
    return {
      ...song,
      strength: currentStrength + drift + diffusion + commonShockValue
    };
  });
  
  // ========================================================================
  // STEP 2: Calculate probabilities using softmax with priority bias
  // ========================================================================
  
  const rawScores = updatedSongs.map((song, index) => {
    const priorityBias = calculatePriorityBias(song, index, p, top20Titles);
    return song.strength + priorityBias;
  });
  
  let probs = softmax(rawScores, p.beta);
  
  // Apply probability smoothing (makes changes less jerky)
  if (p.probSmoothing > 0 && prevProbs && prevProbs.length === probs.length) {
    const smoothing = clamp(p.probSmoothing, 0, 0.95);
    probs = probs.map((prob, i) => smoothing * prevProbs[i] + (1 - smoothing) * prob);
    // Re-normalize
    const sum = probs.reduce((a, b) => a + b, 0) || 1;
    probs = probs.map(p => p / sum);
  }
  
  // ========================================================================
  // STEP 3: Simulate "votes" based on noise model
  // ========================================================================
  
  // Apply crowd size jitter
  const crowdJitter = p.crowdJitterPct > 0 ? (1 + p.crowdJitterPct * randn()) : 1;
  const effectiveVotesPerPeriod = 100 * Math.max(0, crowdJitter); // Base of 100 "votes"
  
  let songsWithVotes = updatedSongs.map((song, i) => {
    const expectedVotes = effectiveVotesPerPeriod * probs[i];
    let actualVotes = expectedVotes;
    
    if (p.noiseModel === 'poisson') {
      actualVotes = samplePoisson(expectedVotes);
    } else if (p.noiseModel === 'gaussian') {
      const variance = Math.max(expectedVotes, expectedVotes * (1 - probs[i]));
      actualVotes = Math.max(0, expectedVotes + Math.sqrt(variance) * randn());
    }
    // else 'none' - use expected value directly
    
    return {
      ...song,
      periodVotes: actualVotes
    };
  });
  
  // ========================================================================
  // STEP 4: Apply burst mechanics (popularity waves)
  // ========================================================================
  
  const burstProb = 1 - Math.exp(-p.burstProbPerSec * dt);
  if (Math.random() < burstProb) {
    const targetIndex = weightedChoice(probs);
    const burstSize = Math.max(0, p.burstMean * Math.max(0, 1 + p.burstJitterPct * randn()));
    
    songsWithVotes = songsWithVotes.map((song, i) => {
      if (i === targetIndex) {
        if (debugMode) {
          console.log(`🌊 Popularity wave! ${song.title} +${burstSize.toFixed(0)} votes`);
        }
        return { ...song, periodVotes: song.periodVotes + burstSize };
      }
      return song;
    });
  }
  
  // ========================================================================
  // STEP 5: Apply jump mechanics (sudden attention spikes)
  // ========================================================================
  
  const jumpProb = 1 - Math.exp(-p.jumpProbPerSec * dt);
  if (Math.random() < jumpProb && p.jumpStd > 0) {
    const targetIndex = Math.floor(Math.random() * songsWithVotes.length);
    const jumpMagnitude = p.jumpStd * randn();
    
    songsWithVotes = songsWithVotes.map((song, i) => {
      if (i === targetIndex) {
        if (debugMode) {
          console.log(`⚡ Attention jump! ${song.title} strength ${jumpMagnitude > 0 ? '+' : ''}${jumpMagnitude.toFixed(2)}`);
        }
        return { ...song, strength: song.strength + jumpMagnitude };
      }
      return song;
    });
  }
  
  // ========================================================================
  // STEP 6: Update scores based on ranking mode
  // ========================================================================
  
  let finalSongs;
  
  if (p.rankingMode === 'cumulative') {
    // CUMULATIVE MODE: Accumulate votes over time for organic, gradual changes
    finalSongs = songsWithVotes.map(song => {
      // Use previous state totalVotes, or fall back to song's current totalVotes (from Firebase), or default to 0
      const previousTotal = prevTotalVotes[song.title] !== undefined 
        ? prevTotalVotes[song.title] 
        : (song.totalVotes || 0);
      const newTotal = previousTotal + song.periodVotes;
      
      return {
        ...song,
        totalVotes: newTotal,
        periodVotes: song.periodVotes, // Keep for debugging
        score: newTotal // Use totalVotes as score for ranking
      };
    });
  } else {
    // SNAPSHOT MODE: Traditional EMA scoring (can cause sudden jumps)
    finalSongs = songsWithVotes.map(song => {
      // Normalize votes to [0, 1] range
      const maxVotes = Math.max(...songsWithVotes.map(s => s.periodVotes)) || 1;
      const normalizedVotes = song.periodVotes / maxVotes;
      
      // EMA update: score = α * new + (1-α) * old
      const newScore = p.alpha * normalizedVotes + (1 - p.alpha) * song.score;
      
      return {
        ...song,
        score: clamp(newScore, 0, 1),
        periodVotes: song.periodVotes
      };
    });
  }
  
  // ========================================================================
  // STEP 7: Sort and assign ranks
  // ========================================================================
  
  const sorted = [...finalSongs].sort((a, b) => b.score - a.score);
  const rankedSongs = sorted.map((song, index) => ({
    ...song,
    rank: index + 1
  }));
  
  // ========================================================================
  // STEP 8: Store state for next iteration
  // ========================================================================
  
  const newState = {
    strengths: {},
    probs: probs,
    totalVotes: {} // Store cumulative votes
  };
  
  rankedSongs.forEach(song => {
    newState.strengths[song.title] = song.strength;
    if (p.rankingMode === 'cumulative' && song.totalVotes !== undefined) {
      newState.totalVotes[song.title] = song.totalVotes;
    }
  });
  
  return {
    songs: rankedSongs,
    state: newState
  };
}

/**
 * Check if simulation should run (not blocked by admin override)
 */
export function shouldRunSimulation(lastAdminUpdate, isManualOverride, adminOverrideDelay) {
  if (!isManualOverride) return true;
  
  const delay = adminOverrideDelay || DEFAULT_SIMULATION_PARAMS.adminOverrideDelay;
  const timeSinceAdminUpdate = Date.now() - new Date(lastAdminUpdate).getTime();
  return timeSinceAdminUpdate > delay;
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  softmax,
  clamp,
  randn,
  samplePoisson,
  weightedChoice,
  calculatePriorityBias
};
